// Графики — Observable Plot (ISC, 0.6.17).
//
// ПОЧЕМУ БИБЛИОТЕКА, А НЕ РУКОПИСНЫЙ SVG. Прежняя версия рисовала всё
// сама и упиралась в три вещи, которые своим кодом стоят дороже, чем
// сто килобайт зависимости: подсказки при наведении жили на onMouseEnter
// каждого прямоугольника, выделения области и зума не было вовсе, а
// фиксированный `viewBox` шириной 480 на широком мониторе рисовал график
// в середине пустого поля — `preserveAspectRatio` вписывал его по высоте.
// Plot получает настоящую ширину контейнера и раскладывает оси заново.
//
// ПОЧЕМУ ИМЕННО PLOT, А НЕ ECHARTS. Три причины, по убыванию веса:
//
//   1. Цвета. Plot считает строку `var(--series-a)` допустимым цветом
//      (options.js, isColor) и кладёт её в атрибут SVG как есть, поэтому
//      палитра остаётся в styles.css под тестом контраста. ECharts рисует
//      в canvas, переменных CSS не видит и потребовал бы второй палитры
//      в JS — ровно того, что здесь запрещено.
//   2. Вес. Plot 348 КБ / 101 КБ в gzip; ECharts даже с ручной сборкой
//      только нужных серий — 834 КБ / 233 КБ, полный — 1699 КБ / 470 КБ.
//   3. Целые значения. `Plot.rectY` с явными x1/x2 ставит столбик РОВНО
//      на целом: «7 врагов», а не «6.5–7.5». Домен остаётся линейным,
//      поэтому работает и обратное преобразование пикселя в значение —
//      на нём стоит выделение области.
//
// Чего у Plot нет из коробки — протяжки мышью; она в PlotFigure, сорок
// строк на pointer-событиях. Это дешевле, чем 132 КБ разницы в gzip
// плюс дубликат палитры.

import { useMemo, useState } from 'react';
import * as Plot from '@observablehq/plot';
import type { FrequencyRow, HistogramBin, Percentiles } from '../lib/stats';
import { describe } from '../lib/stats';
import { num } from '../lib/format';
import { PlotFigure, type PlotOptions } from './PlotFigure';

// Цвета берутся из палитры темы через var(): переключение темы —
// обычный каскад CSS, без getComputedStyle и без перерисовки графика.
const AXIS = 'var(--chart-axis)';
const GRID = 'var(--chart-grid)';
const LABEL = 'var(--chart-label)';
const VALUE = 'var(--chart-value)';
const MARK = 'var(--chart-mark)';
const MARK_WEAK = 'var(--chart-mark-weak)';
const TIP_BG = 'var(--bg-1)';

/**
 * Общий стиль всех графиков.
 *
 * `color` — это currentColor для осей и подсказок Plot; кегль задан
 * переменной, а не числом, чтобы пол в 11px проверялся тем же тестом,
 * что и остальная шкала (contrast.test.ts).
 */
const PLOT_STYLE = {
  background: 'transparent',
  color: LABEL,
  fontSize: 'var(--fs-chart)',
  fontVariantNumeric: 'tabular-nums',
} as const;

const tipMark = (mark: Plot.Markish): Plot.Markish => mark;

/**
 * Подпись на оси.
 *
 * Дробное значение показывается с десятой, а не округляется: на узком
 * домене (запас патронов лежит между −6 и −4) d3 расставляет засечки
 * через полделения, и округление превращало ось в «−6 −6 −5 −5 −5 −4»,
 * то есть в шесть подписей вместо трёх и в неправду про данные.
 */
const tickNum = (value: number): string =>
  Number.isInteger(value) ? num(value) : num(value, 1);

/**
 * Целые засечки с шагом 1, 2, 5, 10, 20… — столько, чтобы влезло около
 * `target` штук. Величины здесь целочисленные (патроны, комнаты, клетки),
 * и ось, размеченная через 2.5 патрона, врёт о природе величины.
 */
function integerTicks(lo: number, hi: number, target: number): number[] {
  const span = Math.max(1, hi - lo);
  const raw = span / Math.max(1, target);
  const magnitude = Math.pow(10, Math.floor(Math.log10(Math.max(raw, 1e-9))));
  const step =
    [1, 2, 5, 10].map((m) => m * magnitude).find((candidate) => candidate >= raw) ??
    10 * magnitude;
  const snapped = Math.max(1, Math.round(step));
  const ticks: number[] = [];
  for (let v = Math.ceil(lo / snapped) * snapped; v <= hi; v += snapped) ticks.push(v);
  return ticks;
}

// --- гистограмма -------------------------------------------------------------

export interface HistogramProps {
  bins: HistogramBin[];
  stats: Percentiles;
  color?: string;
  /** Второй ряд для сравнения. Рисуется контуром поверх первого. */
  overlay?: HistogramBin[] | null;
  overlayColor?: string;
  height?: number;
  unit?: string;
}

/**
 * Гистограмма по целым значениям с линиями перцентилей.
 *
 * Перцентили нарисованы прямо на графике, потому что среднее без них —
 * главный способ соврать о распределении, а разносить их по разным
 * местам значит гарантировать, что смотреть будут только на столбики.
 *
 * Протяжкой мышью выделяется участок оси — домен сужается до него;
 * двойной щелчок или кнопка возвращают весь размах. На площади пола с
 * восемью десятками значений это единственный способ разглядеть хвост.
 */
export function Histogram({
  bins,
  stats,
  color = 'var(--series-a)',
  overlay = null,
  overlayColor = 'var(--series-b)',
  height = 220,
  unit = '',
}: HistogramProps) {
  const [zoom, setZoom] = useState<[number, number] | null>(null);

  const all = useMemo(() => (overlay ? [...bins, ...overlay] : bins), [bins, overlay]);

  const options = useMemo<PlotOptions | null>(() => {
    if (all.length === 0) return null;

    const lo = Math.min(...all.map((b) => b.value));
    const hi = Math.max(...all.map((b) => b.value));
    const domain: [number, number] = zoom ?? [lo - 0.5, hi + 0.5];

    // Подписей на оси не больше двенадцати — дальше они слипаются, и
    // читаемость числа здесь дороже полноты. Шаг всегда целый: ось,
    // размеченная через 2.5 врага, врёт о природе величины.
    const span = Math.max(1, Math.ceil(domain[1]) - Math.floor(domain[0]));
    const step = Math.max(1, Math.ceil(span / 12));
    const ticks: number[] = [];
    for (let v = Math.ceil(domain[0]); v <= Math.floor(domain[1]); v += step) ticks.push(v);

    const percentiles: Array<{ key: keyof Percentiles; label: string; strong: boolean; row: number }> = [
      { key: 'p10', label: 'p10', strong: false, row: 0 },
      { key: 'p25', label: 'p25', strong: false, row: 1 },
      { key: 'p50', label: 'p50', strong: true, row: 2 },
      { key: 'p75', label: 'p75', strong: false, row: 1 },
      { key: 'p90', label: 'p90', strong: false, row: 0 },
    ];
    const markData = percentiles
      .map((m) => ({ ...m, value: stats[m.key] as number }))
      .filter((m) => Number.isFinite(m.value));

    const marks: Plot.Markish[] = [
      Plot.gridY({ stroke: GRID, strokeOpacity: 1 }),
      Plot.rectY(bins, {
        x1: (d: HistogramBin) => d.value - 0.5,
        x2: (d: HistogramBin) => d.value + 0.5,
        y: 'count',
        fill: color,
        fillOpacity: 0.62,
        inset: 0.5,
      }),
    ];

    if (overlay && overlay.length > 0) {
      marks.push(
        Plot.rectY(overlay, {
          x1: (d: HistogramBin) => d.value - 0.5,
          x2: (d: HistogramBin) => d.value + 0.5,
          y: 'count',
          fill: 'none',
          stroke: overlayColor,
          strokeWidth: 1,
          inset: 0.5,
        }),
      );
    }

    marks.push(
      Plot.ruleX(
        markData.filter((m) => !m.strong).map((m) => m.value),
        { stroke: MARK_WEAK, strokeDasharray: '2 3' },
      ),
      Plot.ruleX(
        markData.filter((m) => m.strong).map((m) => m.value),
        { stroke: MARK, strokeWidth: 1.4 },
      ),
      // Подписи перцентилей стоят в три ряда: на тесном распределении
      // p10, p25 и p50 приходятся на одну-две точки, и в один ряд они
      // склеивались в «p1025». Ряд — это отдельный mark, а не канал:
      // `dy` у Plot константа, и по-другому его не сдвинуть.
      ...[0, 1, 2].map((row) =>
        Plot.text(
          markData.filter((m) => m.row === row),
          {
            x: 'value',
            text: 'label',
            frameAnchor: 'top',
            textAnchor: 'start',
            dx: 3,
            dy: 4 + row * 14,
            fill: (d: { strong: boolean }) => (d.strong ? MARK : LABEL),
          },
        ),
      ),
      Plot.ruleY([0], { stroke: AXIS }),
      tipMark(
        Plot.tip(
          bins,
          Plot.pointerX({
            x: 'value',
            y: 'count',
            fill: TIP_BG,
            stroke: 'var(--line)',
            title: (d: HistogramBin) =>
              `${num(d.value)}${unit ? ' ' + unit : ''}\n${num(d.count)} прогонов`,
          }),
        ),
      ),
    );

    return {
      height,
      marginLeft: 52,
      marginRight: 14,
      marginTop: 16,
      marginBottom: 30,
      style: PLOT_STYLE,
      x: { domain, label: null, ticks, tickFormat: tickNum, nice: false },
      y: { label: null, grid: false, tickFormat: tickNum },
      color: { type: 'identity' },
      marks,
    } as PlotOptions;
  }, [all, bins, overlay, overlayColor, color, stats, height, unit, zoom]);

  if (options === null) return <div className="empty">нет данных</div>;

  return (
    <div>
      <PlotFigure
        options={options}
        onSelect={(from, to) => setZoom([from, to])}
        onResetSelection={() => setZoom(null)}
        ariaLabel="гистограмма распределения"
      />

      <div className="percentile-row">
        <span>
          мин <b>{num(stats.min)}</b>
        </span>
        <span>
          p10 <b>{num(stats.p10)}</b>
        </span>
        <span>
          p25 <b>{num(stats.p25)}</b>
        </span>
        <span>
          p50 <b>{num(stats.p50)}</b>
        </span>
        <span>
          p75 <b>{num(stats.p75)}</b>
        </span>
        <span>
          p90 <b>{num(stats.p90)}</b>
        </span>
        <span>
          макс <b>{num(stats.max)}</b>
        </span>
        <span className="faint">
          среднее {num(stats.mean, 2)} {unit}
        </span>
        {zoom === null ? (
          <span className="faint">протяните мышью, чтобы приблизить участок</span>
        ) : (
          <span className="warn">
            показан участок {num(Math.ceil(zoom[0]))}…{num(Math.floor(zoom[1]))}{' '}
            <button className="ghost" onClick={() => setZoom(null)}>
              весь размах
            </button>
          </span>
        )}
      </div>
    </div>
  );
}

// --- ящик с усами ------------------------------------------------------------

export interface BoxRow {
  label: string;
  /** Сырые значения, а не перцентили: коробку считает сам Plot. */
  values: number[];
  color?: string;
}

export interface BoxPlotsProps {
  rows: BoxRow[];
  /** Нарисовать риску на нуле. Нужно там, где знак величины и есть новость. */
  zeroLine?: boolean;
  unit?: string;
}

/**
 * Ящики с усами в общей системе координат — `Plot.boxX`.
 *
 * ГРАНИЦЫ УСОВ ЗДЕСЬ ТУКИ (1.5 межквартильного размаха), А НЕ p10/p90,
 * как рисовал прежний рукописный вариант. Это сознательная замена, а не
 * недосмотр: усы Туки тем и отличаются, что всё, что за них вышло,
 * остаётся на графике отдельными точками. Прежние усы p10…p90 просто
 * обрезали десятую часть выборки с каждой стороны, и хвост, ради
 * которого ящик и смотрят, был не виден. Числа p10…p90 при этом никуда
 * не делись — они в таблице перцентилей на той же странице.
 */
export function BoxPlots({ rows, zeroLine = false, unit = '' }: BoxPlotsProps) {
  const usable = useMemo(() => rows.filter((row) => row.values.length > 0), [rows]);

  const options = useMemo<PlotOptions | null>(() => {
    if (usable.length === 0) return null;

    const flat = usable.flatMap((row) =>
      row.values.map((value) => ({
        label: row.label,
        value,
        color: row.color ?? 'var(--series-a)',
      })),
    );
    const medians = usable.map((row) => {
      const stats = describe(row.values);
      return { label: row.label, stats, color: row.color ?? 'var(--series-a)' };
    });

    const marks: Plot.Markish[] = [
      Plot.gridX({ stroke: GRID, strokeOpacity: 1 }),
      Plot.boxX(flat, {
        x: 'value',
        y: 'label',
        fill: 'color',
        fillOpacity: 0.22,
        stroke: 'color',
        strokeWidth: 1.6,
        r: 1.6,
        sort: null,
      } as Plot.BoxXOptions),
      Plot.text(medians, {
        y: 'label',
        text: (d: { stats: Percentiles }) => num(d.stats.p50),
        frameAnchor: 'right',
        textAnchor: 'end',
        dx: 52,
        fill: VALUE,
      } as Plot.TextOptions),
      tipMark(
        Plot.tip(
          medians,
          Plot.pointerY({
            y: 'label',
            x: (d: { stats: Percentiles }) => d.stats.p50,
            fill: TIP_BG,
            stroke: 'var(--line)',
            title: (d: { label: string; stats: Percentiles }) =>
              `${d.label}\nмин ${num(d.stats.min)} · p25 ${num(d.stats.p25)}\n` +
              `медиана ${num(d.stats.p50)}${unit ? ' ' + unit : ''}\n` +
              `p75 ${num(d.stats.p75)} · макс ${num(d.stats.max)}`,
          }),
        ),
      ),
    ];

    if (zeroLine) {
      marks.splice(1, 0, Plot.ruleX([0], { stroke: MARK, strokeDasharray: '3 3', strokeWidth: 1.2 }));
    }

    // Домен и засечки считаются здесь, а не отдаются d3: на узком домене
    // (запас патронов лежит между −6 и −4) автоматические засечки идут
    // через полделения, а половины патрона не бывает.
    const flatValues = usable.flatMap((row) => row.values);
    const lo = Math.min(...flatValues, zeroLine ? 0 : Number.POSITIVE_INFINITY);
    const hi = Math.max(...flatValues, zeroLine ? 0 : Number.NEGATIVE_INFINITY);
    const pad = Math.max(1, (hi - lo) * 0.04);

    return {
      height: usable.length * 34 + 46,
      marginLeft: 168,
      marginRight: 60,
      marginTop: 8,
      marginBottom: 30,
      style: PLOT_STYLE,
      x: {
        domain: [lo - pad, hi + pad],
        label: null,
        ticks: integerTicks(lo, hi, 10),
        tickFormat: tickNum,
        nice: false,
      },
      y: { domain: usable.map((row) => row.label), label: null },
      color: { type: 'identity' },
      marks,
    } as PlotOptions;
  }, [usable, zeroLine, unit]);

  if (options === null) return <div className="empty">нет данных</div>;
  return <PlotFigure options={options} ariaLabel="ящики с усами" />;
}

// --- полоса долей ------------------------------------------------------------

/** Горизонтальная полоса долей: исходы, причины смерти, веса лут-таблицы. */
export function ShareBar({
  parts,
}: {
  parts: Array<{ label: string; value: number; color: string }>;
}) {
  const total = parts.reduce((sum, p) => sum + p.value, 0);

  const options = useMemo<PlotOptions | null>(() => {
    if (total <= 0) return null;
    return {
      height: 18,
      marginLeft: 0,
      marginRight: 0,
      marginTop: 0,
      marginBottom: 0,
      style: PLOT_STYLE,
      x: { axis: null },
      y: { axis: null },
      color: { type: 'identity' },
      marks: [
        Plot.barX(parts, {
          x: 'value',
          fill: 'color',
          title: (d: { label: string; value: number }) =>
            `${d.label}: ${num(d.value)} (${((d.value / total) * 100).toFixed(1)}%)`,
        }),
      ],
    } as PlotOptions;
  }, [parts, total]);

  if (options === null) return <div className="empty">нет данных</div>;

  return (
    <div>
      <PlotFigure options={options} ariaLabel="доли" />
      <div className="legend">
        {parts.map((part) => (
          <span key={part.label}>
            <i style={{ background: part.color }} />
            {part.label} <span className="mono">{num(part.value)}</span>
          </span>
        ))}
      </div>
    </div>
  );
}

// --- строка-спарклайн частот -------------------------------------------------

export interface SparklineProps {
  rows: FrequencyRow[];
  stats: Percentiles;
  unit?: string;
  /** Фиксированная ширина. Без неё спарклайн занимает всю колонку. */
  width?: number;
  height?: number;
  color?: string;
  /** Доля, ниже которой столбик считается редким и красится в янтарь. */
  rareShare?: number;
}

/**
 * Форма распределения одной величины в одну строку.
 *
 * ПОЧЕМУ НЕ ЯЩИК С УСАМИ. Ящик требует общей шкалы, чтобы его можно было
 * с чем-то сравнить, а на обзоре величины разнородные: врагов шесть,
 * площадь пола под сотню. В общей шкале восемь коробок из девяти
 * вырождаются в точку у левого края. К тому же восемь из девяти величин
 * дискретные и тесные: усы рисуют им хвост, которого нет.
 *
 * Спарклайн частот честнее по обеим причинам: каждая величина в СВОЕЙ
 * шкале, а столбик стоит на целом значении, а не на придуманной корзине.
 *
 * ШИРИНА ТЕПЕРЬ ТЯНЕТСЯ, и прежнее возражение против этого снято. Оно
 * было верно для растянутого `viewBox`: там вместе с шириной множилась
 * и высота, и разница частот в триста раз превращалась в одинаковые
 * плиты. Plot пересчитывает раскладку в настоящих пикселях — шире
 * становятся только столбики, высота остаётся высотой.
 */
export function FrequencySparkline({
  rows,
  stats,
  unit = '',
  width,
  height = 48,
  color = 'var(--series-a)',
  rareShare = 0.01,
}: SparklineProps) {
  const options = useMemo<PlotOptions | null>(() => {
    if (rows.length === 0) return null;
    const lo = rows[0]?.value ?? 0;
    const hi = rows[rows.length - 1]?.value ?? lo;
    const data = rows.map((row) => ({
      ...row,
      fillColor: row.share < rareShare ? MARK : color,
    }));

    return {
      height,
      marginLeft: 0,
      marginRight: 0,
      marginTop: 3,
      marginBottom: 0,
      style: PLOT_STYLE,
      x: { domain: [lo - 0.5, hi + 0.5], axis: null, nice: false },
      y: { axis: null },
      color: { type: 'identity' },
      marks: [
        Plot.rectY(data, {
          x1: (d: FrequencyRow) => d.value - 0.5,
          x2: (d: FrequencyRow) => d.value + 0.5,
          y: 'count',
          fill: 'fillColor',
          fillOpacity: 0.75,
          inset: 0.4,
        }),
        Plot.ruleX([stats.p50], { stroke: MARK, strokeWidth: 1.2, strokeOpacity: 0.9 }),
        tipMark(
          Plot.tip(
            data,
            Plot.pointerX({
              x: 'value',
              y: 'count',
              fill: TIP_BG,
              stroke: 'var(--line)',
              title: (d: FrequencyRow) =>
                `${num(d.value)}${unit ? ' ' + unit : ''}\n` +
                `${num(d.count)} прогонов · ${(d.share * 100).toFixed(1)}%`,
            }),
          ),
        ),
      ],
    } as PlotOptions;
  }, [rows, stats.p50, unit, height, color, rareShare]);

  if (options === null) return <div className="faint mono">нет данных</div>;

  const lo = rows[0]?.value ?? 0;
  const hi = rows[rows.length - 1]?.value ?? lo;

  return (
    <div className="spark">
      <PlotFigure options={options} width={width} ariaLabel="форма распределения" />
      <div className="spark-scale">
        <span className="faint">{num(lo)}</span>
        <span className="faint">{num(rows.length)} знач.</span>
        <span className="faint">{num(hi)}</span>
      </div>
    </div>
  );
}
